import * as z from 'zod/mini'
import { endAfterStart, isoDateOrDateTime, isoDateTime, queryNumber, timeOfDay } from '../../schemas/common'
import { entityFields } from '../../schemas/entity'
import { DiscountType, EmployeeAbsenceType, Month } from '../../types'

/*
 * Request schemas whose only receivers are the controllers in `apps/api/.../controllers/_not-for-demo/`
 * and the services in `services/old-to-redo/`. `main.module.ts` registers none of them, so as of
 * 2026-09-19 no route accepts these - the screens that send them (timesheet, absences, invoice
 * activities, payments, team) get a 404. They are kept, converted, until the product decides the
 * fate of those screens; see "Migration plan" in docs/validation.md.
 */

// --- payments -------------------------------------------------------------------------------------

export const createPaymentForUsersBody = z.object({
  name: z.optional(z.string()),
  description: z.optional(z.string()),
  amount: z.number().check(z.positive()),
  dueDate: z.optional(isoDateTime),
  customerIds: z.array(z.uuid()),
  eventId: z.optional(z.uuid()),
  isPaymentForCourse: z.optional(z.boolean()),
  isPaymentForEventInstance: z.optional(z.boolean()),
  individualDiscount: z.optional(z.boolean()),
  discountAmount: z.optional(z.number().check(z.positive())),
  discountType: z.optional(z.enum(DiscountType)),
  discountId: z.optional(z.uuid()),
})
export type CreatePaymentForUsersBody = z.infer<typeof createPaymentForUsersBody>

export const acceptPaymentsBody = z.object({
  paymentIds: z.array(z.uuid()),
  paidAt: isoDateTime,
})
export type AcceptPaymentsBody = z.infer<typeof acceptPaymentsBody>

export const cancelPaymentsBody = z.object({ paymentIds: z.array(z.uuid()) })
export type CancelPaymentsBody = z.infer<typeof cancelPaymentsBody>

export const getCoursePricingQuery = z.object({
  eventId: z.uuid(),
  userId: z.optional(z.uuid()),
  customerId: z.optional(z.uuid()),
})
export type GetCoursePricingQuery = z.infer<typeof getCoursePricingQuery>

// --- invoice activities and timesheets ------------------------------------------------------------

const invoiceActivityName = z.string().check(z.trim(), z.minLength(2), z.maxLength(40))

export const createInvoiceActivityBody = z.object({ name: invoiceActivityName })
export type CreateInvoiceActivityBody = z.infer<typeof createInvoiceActivityBody>

export const editInvoiceActivityBody = z.object({ name: z.optional(invoiceActivityName) })
export type EditInvoiceActivityBody = z.infer<typeof editInvoiceActivityBody>

export const saveCustomerInvoiceActivitiesBody = z.object({ invoiceActivityIds: z.array(z.uuid()) })
export type SaveCustomerInvoiceActivitiesBody = z.infer<typeof saveCustomerInvoiceActivitiesBody>

/** `.../invoices/:year/:month/...` - both arrive as strings. */
const invoicePeriod = {
  year: queryNumber(z.int().check(z.positive())),
  month: queryNumber(z.enum(Month)),
}

export const getEmployeeInvoicesParams = z.object({ ...invoicePeriod, employeeId: z.uuid() })
export type GetEmployeeInvoicesParams = z.infer<typeof getEmployeeInvoicesParams>

export const getUserEmployeeInvoicesParams = z.object(invoicePeriod)
export type GetUserEmployeeInvoicesParams = z.infer<typeof getUserEmployeeInvoicesParams>

export const getEmployeeInvoicesQuery = z.object(invoicePeriod)
export type GetEmployeeInvoicesQuery = z.infer<typeof getEmployeeInvoicesQuery>

export const getOrCreateEmployeeInvoiceBody = z.object({
  year: z.int().check(z.positive()),
  month: z.enum(Month),
  employeeId: z.uuid(),
})
export type GetOrCreateEmployeeInvoiceBody = z.infer<typeof getOrCreateEmployeeInvoiceBody>

export const newInvoiceItemBody = z.object({
  /** Day of the month. */
  day: z.int().check(z.minimum(1), z.maximum(31)),
  startTime: timeOfDay,
  endTime: timeOfDay,
  invoiceActivityId: z.optional(z.uuid()),
  eventInstanceId: z.optional(z.uuid()),
})
export type NewInvoiceItemBody = z.infer<typeof newInvoiceItemBody>

export const createEmployeeAbsenceBody = z
  .object({
    start: isoDateTime,
    end: isoDateTime,
    type: z.enum(EmployeeAbsenceType),
  })
  .check(z.superRefine(endAfterStart('start', 'end')))
export type CreateEmployeeAbsenceBody = z.infer<typeof createEmployeeAbsenceBody>

// --- calendars, events and trials (services/old-to-redo) ------------------------------------------

const calendarWindow = {
  start: z.optional(isoDateOrDateTime),
  end: z.optional(isoDateOrDateTime),
}

export const getCalendarEventsForEntityQuery = z.object(calendarWindow)
export type GetCalendarEventsForEntityQuery = z.infer<typeof getCalendarEventsForEntityQuery>

export const getCalendarEventsForUserQuery = z.object({ ...calendarWindow, userId: z.optional(z.uuid()) })
export type GetCalendarEventsForUserQuery = z.infer<typeof getCalendarEventsForUserQuery>

export const joinEventBody = z.object({
  userId: z.optional(z.uuid()),
  isTrial: z.optional(z.boolean()),
})
export type JoinEventBody = z.infer<typeof joinEventBody>

export const eventInstanceAttendanceBody = z.object({
  eventInstanceId: z.uuid(),
  unattendedCustomerIds: z.array(z.uuid()),
})
export type EventInstanceAttendanceBody = z.infer<typeof eventInstanceAttendanceBody>

export const canJoinTrialBody = z.object({
  instanceId: z.uuid(),
  customerId: z.uuid(),
})
export type CanJoinTrialBody = z.infer<typeof canJoinTrialBody>

// --- premises -------------------------------------------------------------------------------------

/** A user registering their own premise names it. */
export const createEntityByUserBody = z.object({ name: entityFields.name })
export type CreateEntityByUserBody = z.infer<typeof createEntityByUserBody>

/** An admin registering a premise names its owner. */
export const createEntityByAdminBody = z.object({ ownerId: z.uuid() })
export type CreateEntityByAdminBody = z.infer<typeof createEntityByAdminBody>

// --- lists ----------------------------------------------------------------------------------------

export const paginatedQuery = z.object({
  limit: z.optional(queryNumber(z.int().check(z.positive()))),
  offset: z.optional(queryNumber(z.int().check(z.nonnegative()))),
})
export type PaginatedQuery = z.infer<typeof paginatedQuery>
